// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, type ApiRequestError } from '../../api/client';
import type {
  Buildup,
  ProjectInfo,
  ReportPdfVersion,
  Resource,
  ResourceCreate,
  ResourcePatchResult,
  ReviewStatus,
  Sample,
  SampleCreate,
  SamplePatch,
  Survey,
  SurveyFractions,
  SurveyMeasureReport,
  SurveyPart,
  SurveyPartPatch,
  SurveyPhotos,
  SurveySummary,
  SurveySyncReport,
  SurveyType,
  SurveyTypeCreate,
  SurveyTypeDeletion,
  SurveyTypeMergeResult,
  SurveyTypePatch,
  ResourceKey,
  Template,
} from '../../api/types';
import {
  REJECTED_MESSAGE,
  approvedMessage,
  blockedMessage,
  syncMessage,
  typeDeletedMessage,
} from '../../kortlaegning/copy';
import {
  readTypeColors,
  setTypeColorIn,
  tracksTypeColor,
  withLocalColors,
  writeTypeColors,
  type TypeColorMap,
  type TypeColorStore,
} from '../../bygning/typeColors';
import { MEASURE_UNSUPPORTED, measureLines, measureMessage } from '../../bygning/measure';
import type { Maybe } from '../../bygning/parts';
import {
  dismissedParts,
  liveTypes,
  removePart,
  removeType,
  replacePart,
  replaceType,
  tracksDismissed,
  type DismissedPart,
} from '../../kortlaegning/model';
import { photoBatchKey } from '../../kortlaegning/photo';
import { patchedResources, replaceResources, touchesSurvey } from '../../kortlaegning/resources';
import { cellErrorMessage, refreshAfterSave } from '../../kortlaegning/writeOutcome';
import { addSample, gateChanges, gateMessage, linksToSend, removeSample, replaceSample, type SampleLinks } from '../../miljoe/model';
import { newRecordId, type ProjectPatch } from '../../overblik/model';
import { generateErrorMessage, generatedToast } from '../../rapport/model';
import { useCanEdit } from '../CaseRoleContext';
import { useJobs } from '../JobsContext';
import { saveErrorMessage } from '../saveError';
import { useSurveyCounts } from '../SurveyCountsContext';
import { useAsync } from '../useAsync';
import { useMutationQueue } from '../useMutationQueue';
import { appWriteChain } from '../writeChain';

/**
 * Everything the six Bygning modes read, loaded once for the shell. `types`
 * is the live copy every write folds its response into; `survey` is the same
 * list with counts recomputed from it, so the tab badges follow an approval
 * without a re-read.
 */
export interface CaseData {
  survey: Survey | null;
  /** The survey's types without their dismissed parts (what every view counts and picks). */
  types: SurveyType[];
  /** Parts marked as an error ("Fejldetektion · Gendan" in Tabel › Dele), by code. */
  dismissed: DismissedPart[];
  /**
   * The server tracks `SurveyPart.dismissed` (ruxd-6tz): "Markér som fejl" is
   * a reversible PATCH. False on an older server, where it deletes the part.
   */
  dismissSupported: boolean;
  /**
   * The server keeps `SurveyType.color` (frontend-r22.23). False on an older
   * ruxd: a chosen colour is kept in this browser only (localStorage per
   * case) and already folded into `types`.
   */
  typeColorShared: boolean;
  samples: Sample[];
  summary: SurveySummary | null;
  resourceKeys: ResourceKey[];
  templates: Template[];
  resources: Resource[];
  /** Photo count + best frame per part; null until loaded or when it failed (the quiet placeholder). */
  photos: SurveyPhotos | null;
  /** The case's project metadata record (`GET /projects`, first); null when it has none yet. */
  project: ProjectInfo | null;
  /** Report versions, newest first; null until the first read lands (generate waits for it). */
  reportVersions: ReportPdfVersion[] | null;
  /** Waste fractions for Indberetning; null until loaded or when it failed. */
  fractions: SurveyFractions | null;
  /**
   * The project's Opbygning library (`GET /buildups`, ReUseX schema v37);
   * null until it lands, or for good on a server without the route — the
   * views then fall back to the embedded copy (`bygning/buildups` BUILDUPS)
   * and the frontend's own tonnes.
   */
  buildups: Buildup[] | null;
  /** The last "Mål fra modellen" report of this session (Modelindstillinger › Scanning shows it). */
  measureReport: SurveyMeasureReport | null;
  /** The first load (or a full refresh) is in flight. */
  loading: boolean;
  /** The first load has landed and seeded the copies above. */
  loaded: boolean;
  /** The first load failed; show it with `ErrorBanner` and `actions.refresh`. */
  error: Error | null;
  /** A write is queued or in flight on the app chain. Gates buttons only. */
  busy: boolean;
  /** A report is generating (its own chain: it can take minutes). */
  reportBusy: boolean;
  /** A write succeeded but the re-read after it failed: the view may be stale. */
  staleNotice: string | null;
  /** False for a viewer of the case. */
  canEdit: boolean;
}

export interface ReviewOptions {
  /** Replaces the default 422 handling (the "afventer prøvesvar" toast). */
  onUnprocessable?: (cause: ApiRequestError) => void;
}

export interface SampleWriteOptions {
  /** Toast when the write changed no approval gate (the gate message wins otherwise). */
  fallbackToast?: string;
  onUnprocessable?: (cause: ApiRequestError) => void;
}

/**
 * The writes. Every one runs on the app write chain (`useMutationQueue`), so
 * two writes never land out of order and the next screen's first load waits
 * for them; a failure toasts `saveErrorMessage` (never rejects), and each
 * resolves to the server's answer, or null when it failed. Field-level
 * writes are never gated on `busy`; gate buttons on `data.busy` yourself.
 */
export interface CaseActions {
  /** Re-read everything (after sync, or the error banner's "Prøv igen"). */
  refresh(): void;
  /** One transient toast (the shell's `useToast`). */
  notify(message: string): void;
  /** Sparse type edit. Anything but `review_status` re-reads resources (sys: values, R5). */
  patchType(id: number, patch: SurveyTypePatch, options?: ReviewOptions): Promise<SurveyType | null>;
  /** Godkend / Afvis / tilbage i kø, with the review toasts; a 422 names the open samples. */
  review(id: number, status: ReviewStatus, options?: ReviewOptions): Promise<SurveyType | null>;
  /** Sparse part edit; `type_id` moves it to another type (re-reads the survey). */
  patchPart(code: string, patch: SurveyPartPatch): Promise<SurveyPart | null>;
  /** `POST /survey/types` (an older server ignores note/mass_t/confidence/starred: check the answer). */
  createType(body: SurveyTypeCreate): Promise<SurveyType | null>;
  /**
   * `POST /survey/types/{id}/merge {into}`: one transaction server-side;
   * `'unsupported'` (nothing written, nothing toasted) when the server lacks
   * the route — `runMerge` then falls back to the step plan.
   */
  mergeType(id: number, into: number): Promise<Maybe<SurveyTypeMergeResult>>;
  /**
   * One patch for several types (`PATCH /survey/types`, one transaction); on
   * a server without the batch route, one PATCH per type in order (stops at
   * the first failure). Resolves the edited types, or null when it failed.
   */
  patchTypes(ids: readonly number[], patch: SurveyTypePatch): Promise<SurveyType[] | null>;
  /** Delete a type and all its parts; toasts how many went with it. */
  deleteType(id: number): Promise<SurveyTypeDeletion | null>;
  /** Resource values by key id; a 400 toasts the key by `label`. `sys:` keys re-read the survey. */
  patchResource(code: string, values: Record<string, string | null>, label?: string): Promise<ResourcePatchResult | null>;
  /** A manual part under an existing type ("Tilføj uden geometri"). */
  createResource(body: ResourceCreate): Promise<Resource | null>;
  /** Delete one part (scan-backed ones are tombstoned so a sync does not re-create them). */
  deleteResource(code: string): Promise<boolean>;
  patchSample(id: number, patch: SamplePatch, options?: SampleWriteOptions): Promise<Sample | null>;
  createSample(body: SampleCreate): Promise<Sample | null>;
  deleteSample(id: number): Promise<boolean>;
  /** Replaces the sample's linked types (PUT: the last request wins). */
  /** `PUT /samples/{id}/links`: the full set of whole types and single parts (codes the survey no longer has are dropped). */
  setSampleLinks(id: number, links: SampleLinks): Promise<Sample | null>;
  /** "Opret kortlægning fra instanser"; then re-reads everything. */
  syncSurvey(): Promise<SurveySyncReport | null>;
  /**
   * "Mål fra modellen" (`POST /survey/measure`): the parts' quantities from
   * the walls, openings and slabs, build-ups suggested; then re-reads
   * everything. `'unsupported'` on a server without the route.
   */
  measureSurvey(): Promise<Maybe<SurveyMeasureReport>>;
  /** A new report version (own chain); `templateId` adds the Ressourcetabel section. */
  generateReport(templateId?: number | null): Promise<ReportPdfVersion | null>;
  /** Upserts the project record (an id is minted on the first edit of a case without one). */
  patchProject(patch: ProjectPatch): Promise<ProjectInfo | null>;
  /**
   * A type's Materiale colour (`#rrggbb`, null = automatic): `PATCH
   * /survey/types/{id} {color}` on a server that keeps it, else this
   * browser's localStorage. Resolves false when the write failed.
   */
  setTypeColor(id: number, color: string | null): Promise<boolean>;
}

export interface CaseStore {
  data: CaseData;
  actions: CaseActions;
}

export interface CaseDataOptions {
  /** Shows a toast. */
  notify: (message: string) => void;
}

/** localStorage, or undefined where it is absent or access throws (sandbox). */
function browserStore(): TypeColorStore | undefined {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : undefined;
  } catch {
    return undefined;
  }
}

/** Said when a sample write landed but the gate re-read failed. */
const GATE_STALE = 'Gemt — men miljøstatus kunne ikke genindlæses. Genindlæs for at se den.';

/** A route an older ruxd does not have (404 / 405 / 501), as opposed to a refused write. */
function isMissingRoute(cause: unknown): boolean {
  const status = (cause as ApiRequestError | null)?.status;
  return status === 404 || status === 405 || status === 501;
}

function countsOf(types: readonly SurveyType[]): Survey['counts'] {
  const queue = types.filter((t) => t.review_status === 'queue').length;
  const approved = types.filter((t) => t.review_status === 'approved').length;
  const rejected = types.filter((t) => t.review_status === 'rejected').length;
  return { queue, approved, rejected, all: queue + approved };
}

/** A secondary read: a failure resolves null (a quiet placeholder), an abort still throws. */
function quietly<T>(run: (signal: AbortSignal) => Promise<T>): (signal: AbortSignal) => Promise<T | null> {
  return async (signal) => {
    try {
      return await run(signal);
    } catch (cause) {
      if (signal.aborted) throw cause;
      return null;
    }
  };
}

/** A state value with a synchronous ref mirror, so queued writes fold into what the previous one produced. */
function useMirror<T>(initial: T) {
  const [value, setValue] = useState<T>(initial);
  const ref = useRef<T>(initial);
  const set = useCallback((update: (prev: T) => T): T => {
    ref.current = update(ref.current);
    setValue(ref.current);
    return ref.current;
  }, []);
  return [value, ref, set] as const;
}

/**
 * The Bygning shell's case store: what `KortlaegningPage`, `MiljoePage`,
 * `OverblikPage` and `RapportPage` each loaded and wrote, lifted into one
 * place. First load waits for `appWriteChain.idle()` (R11) so a write queued
 * on the screen just left lands first. Each write replaces the local copy of
 * what it changed, then re-reads what the old pages re-read.
 */
export function useCaseData({ notify }: CaseDataOptions): CaseStore {
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const say = useCallback((message: string) => notifyRef.current(message), []);
  const canEdit = useCanEdit();
  const { refresh: refreshShellCounts, refreshProject: refreshShellProject } = useSurveyCounts();

  const main = useAsync(
    (s) =>
      appWriteChain
        .idle()
        .then(() =>
          Promise.all([
            api.survey(s),
            api.samples(s),
            api.surveySummary(s),
            api.resourceKeys(s),
            api.templates(s),
            api.resources(undefined, s),
            api.projects(s),
          ]),
        ),
    [],
  );
  const versionsRead = useAsync(quietly((s) => appWriteChain.idle().then(() => api.listReportVersions(s))), []);
  const fractionsRead = useAsync(quietly((s) => appWriteChain.idle().then(() => api.surveyFractions(s))), []);
  // A server before ReUseX schema v37 has no library route: null, and the views use the embedded copy.
  const buildupsRead = useAsync(quietly((s) => api.buildups(s)), []);
  const [measureReport, setMeasureReport] = useState<SurveyMeasureReport | null>(null);

  // Every part as the server sends it, dismissed ones included; `types` below is the live view.
  const [allTypes, typesRef, setTypes] = useMirror<SurveyType[]>([]);
  const typeColorShared = useMemo(() => tracksTypeColor(allTypes), [allTypes]);
  // The colours of an older ruxd that does not keep them: this browser's copy, per case.
  const caseId = api.caseId;
  const [localColors, setLocalColors] = useState<TypeColorMap>(() => readTypeColors(browserStore(), caseId));
  useEffect(() => setLocalColors(readTypeColors(browserStore(), caseId)), [caseId]);
  const types = useMemo(
    () => withLocalColors(liveTypes(allTypes), localColors, typeColorShared),
    [allTypes, localColors, typeColorShared],
  );
  const dismissed = useMemo(() => dismissedParts(allTypes), [allTypes]);
  const dismissSupported = useMemo(() => tracksDismissed(allTypes), [allTypes]);
  const [samples, samplesRef, setSamples] = useMirror<Sample[]>([]);
  const [resources, , setResources] = useMirror<Resource[]>([]);
  const [project, projectRef, setProject] = useMirror<ProjectInfo | null>(null);
  const [summary, setSummary] = useState<SurveySummary | null>(null);
  const [resourceKeys, setResourceKeys] = useState<ResourceKey[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [reportVersions, setReportVersions] = useState<ReportPdfVersion[] | null>(null);
  const [fractions, setFractions] = useState<SurveyFractions | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [staleNotice, setStaleNotice] = useState<string | null>(null);

  useEffect(() => {
    const d = main.data;
    if (!d) return;
    setTypes(() => d[0].types);
    setSamples(() => d[1]);
    setSummary(d[2]);
    setResourceKeys(d[3]);
    setTemplates(d[4]);
    setResources(() => d[5]);
    setProject(() => d[6][0] ?? null);
    setStaleNotice(null);
    setLoaded(true);
  }, [main.data, setTypes, setSamples, setResources, setProject]);

  useEffect(() => {
    if (versionsRead.data !== undefined && versionsRead.data !== null) setReportVersions(versionsRead.data);
  }, [versionsRead.data]);
  useEffect(() => {
    if (fractionsRead.data !== undefined) setFractions(fractionsRead.data);
  }, [fractionsRead.data]);

  // Photos follow the set of instance-backed parts; a failure only leaves placeholders.
  const photoKey = photoBatchKey(types);
  const photosRead = useAsync(
    quietly<SurveyPhotos | null>((s) => (photoKey ? api.surveyPhotos(s) : Promise.resolve(null))),
    [photoKey],
  );

  // ---------------------------------------------------------------- reads --

  const refreshSurvey = useCallback(async () => {
    const s = await api.survey();
    return setTypes(() => s.types);
  }, [setTypes]);

  const refreshResources = useCallback(async () => {
    const list = await api.resources();
    setResources(() => list);
  }, [setResources]);

  /** The summary and fractions behind badges, KPIs and Indberetning; silent on failure. */
  const refreshFigures = useCallback(() => {
    api.surveySummary().then(setSummary, () => undefined);
    api.surveyFractions().then(setFractions, () => undefined);
    refreshShellCounts();
  }, [refreshShellCounts]);

  /**
   * The re-read after a write that already succeeded. A failure never undoes
   * the success; it shows the stale-view notice, which a later successful
   * re-read clears.
   */
  const reread = useCallback(async (refresh: () => Promise<unknown>) => {
    if (await refreshAfterSave(refresh, setStaleNotice)) setStaleNotice(null);
  }, []);

  // ------------------------------------------------------------ mutations --

  const { busy, mutate } = useMutationQueue({
    onError: (cause) => say(saveErrorMessage(cause)),
    onSettled: refreshFigures,
  });
  // Report generation changes no survey state and may run for minutes, so it
  // must not hold up other screens' first loads (RapportPage's `scope: 'page'`).
  const reportQueue = useMutationQueue({
    scope: 'page',
    onError: (cause) => say(generateErrorMessage(cause)),
  });
  const mutateReport = reportQueue.mutate;

  const run = useCallback(
    <T>(task: () => Promise<T>, onUnprocessable?: (cause: ApiRequestError) => void): Promise<T | null> => {
      let out: T | null = null;
      return mutate(async () => {
        out = await task();
      }, onUnprocessable).then(() => out);
    },
    [mutate],
  );

  // Set when a gate re-read failed: `typesRef` then predates that write, so
  // the next diff would credit (or blame) this change for the earlier one.
  const gateStaleRef = useRef(false);
  const refreshGate = useCallback(
    async (code: string): Promise<string | null> => {
      const before = typesRef.current;
      try {
        const after = await refreshSurvey();
        if (gateStaleRef.current) {
          gateStaleRef.current = false;
          return null;
        }
        return gateMessage(code, gateChanges(before, after));
      } catch {
        gateStaleRef.current = true;
        return GATE_STALE;
      }
    },
    [refreshSurvey, typesRef],
  );

  const newProjectId = useRef<string | null>(null);

  const refresh = useCallback(() => {
    main.reload();
    versionsRead.reload();
    fractionsRead.reload();
    photosRead.reload();
  }, [main.reload, versionsRead.reload, fractionsRead.reload, photosRead.reload]);

  const actions = useMemo<CaseActions>(
    () => ({
      refresh,
      notify: say,

      patchType: (id, patch, options) =>
        run(async () => {
          const body = await api.patchSurveyType(id, patch);
          setTypes((prev) => replaceType(prev, body));
          if (Object.keys(patch).some((k) => k !== 'review_status')) await reread(refreshResources);
          return body;
        }, options?.onUnprocessable),

      review: (id, status, options) => {
        const before = typesRef.current.find((t) => t.id === id);
        if (!before) return Promise.resolve(null);
        return run(
          async () => {
            const body = await api.patchSurveyType(id, { review_status: status });
            const next = setTypes((prev) => replaceType(prev, body));
            if (status === 'approved') say(approvedMessage(body.name, next));
            else if (status === 'rejected') say(REJECTED_MESSAGE);
            else say(`»${body.name}« er tilbage i køen`);
            return body;
          },
          options?.onUnprocessable ?? (() => say(blockedMessage(before, samplesRef.current))),
        );
      },

      patchPart: (code, patch) =>
        run(async () => {
          const body = await api.patchSurveyPart(code, patch);
          setTypes((prev) => replacePart(prev, body));
          // A move, a new quantity or a dismissal changes the types' sums; the rest only sys: values.
          if (patch.type_id !== undefined || patch.quantity !== undefined || patch.dismissed !== undefined) {
            await reread(() => Promise.all([refreshSurvey(), refreshResources()]));
          } else {
            await reread(refreshResources);
          }
          return body;
        }),

      createType: (body) =>
        run(async () => {
          const created = await api.createSurveyType(body);
          setTypes((prev) => [...prev.filter((t) => t.id !== created.id), created]);
          say(`»${created.name}« oprettet`);
          await reread(refreshResources);
          return created;
        }),

      mergeType: (id, into) =>
        run(async (): Promise<Maybe<SurveyTypeMergeResult>> => {
          let r: SurveyTypeMergeResult;
          try {
            r = await api.mergeSurveyType(id, into);
          } catch (cause) {
            if (isMissingRoute(cause)) return 'unsupported';
            throw cause;
          }
          setTypes((prev) => replaceType(removeType(prev, r.source_id), r.into));
          // Parts, sample links and figures moved with it.
          await reread(async () => {
            const [, list] = await Promise.all([refreshSurvey(), api.samples(), refreshResources()]);
            setSamples(() => list);
          });
          return r;
        }),

      patchTypes: (ids, patch) =>
        run(async () => {
          let out: SurveyType[];
          try {
            out = await api.patchSurveyTypes({ ids: [...ids], patch });
          } catch (cause) {
            if (!isMissingRoute(cause)) throw cause;
            // An older server: one PATCH per type, in order.
            out = [];
            for (const id of ids) {
              const body = await api.patchSurveyType(id, patch);
              setTypes((prev) => replaceType(prev, body));
              out.push(body);
            }
          }
          setTypes((prev) => out.reduce(replaceType, prev));
          if (Object.keys(patch).some((k) => k !== 'review_status')) await reread(refreshResources);
          return out;
        }),

      deleteType: (id) => {
        const name = typesRef.current.find((t) => t.id === id)?.name ?? 'Typen';
        return run(async () => {
          const r = await api.deleteSurveyType(id);
          setTypes((prev) => removeType(prev, id));
          say(typeDeletedMessage(name, r.parts_deleted));
          await reread(() => Promise.all([refreshSurvey(), refreshResources()]));
          return r;
        });
      },

      patchResource: (code, values, label) =>
        run(async () => {
          let body: ResourcePatchResult;
          try {
            body = await api.patchResource(code, values);
          } catch (cause) {
            // A refused value names the key by its label, never the raw key id.
            say(cellErrorMessage(cause, label ?? 'feltet'));
            return null;
          }
          setResources((prev) => replaceResources(prev, patchedResources(body)));
          if (touchesSurvey(Object.keys(values))) await reread(refreshSurvey);
          return body;
        }),

      createResource: (body) =>
        run(async () => {
          const created = await api.createResource(body);
          say(`${created.code} tilføjet`);
          await reread(() => Promise.all([refreshSurvey(), refreshResources()]));
          return created;
        }),

      deleteResource: (code) =>
        run(async () => {
          await api.deleteResource(code);
          setTypes((prev) => removePart(prev, code));
          setResources((prev) => prev.filter((r) => r.code !== code));
          say(`${code} slettet`);
          await reread(() => Promise.all([refreshSurvey(), refreshResources()]));
          return true;
        }).then((ok) => ok === true),

      patchSample: (id, patch, options) =>
        run(async () => {
          const updated = await api.patchSample(id, patch);
          setSamples((prev) => replaceSample(prev, updated));
          const message = (await refreshGate(updated.code)) ?? options?.fallbackToast ?? null;
          if (message) say(message);
          return updated;
        }, options?.onUnprocessable),

      createSample: (body) =>
        run(async () => {
          const created = await api.createSample(body);
          setSamples((prev) => addSample(prev, created));
          say((await refreshGate(created.code)) ?? `${created.code} registreret`);
          return created;
        }),

      deleteSample: (id) => {
        const code = samplesRef.current.find((s) => s.id === id)?.code ?? `Prøve ${id}`;
        return run(async () => {
          await api.deleteSample(id);
          setSamples((prev) => removeSample(prev, id));
          say((await refreshGate(code)) ?? `${code} slettet`);
          return true;
        }).then((ok) => ok === true);
      },

      setSampleLinks: (id, links) =>
        run(async () => {
          const updated = await api.setSampleLinks(id, linksToSend(links, typesRef.current));
          setSamples((prev) => replaceSample(prev, updated));
          const message = await refreshGate(updated.code);
          if (message) say(message);
          return updated;
        }),

      measureSurvey: () =>
        run(async (): Promise<Maybe<SurveyMeasureReport>> => {
          let report: SurveyMeasureReport;
          try {
            report = await api.measureSurvey();
          } catch (cause) {
            if (!isMissingRoute(cause)) throw cause;
            say(MEASURE_UNSUPPORTED);
            return 'unsupported';
          }
          setMeasureReport(report);
          say(measureMessage(report));
          refresh();
          return report;
        }),

      syncSurvey: () =>
        run(async () => {
          const report = await api.syncSurvey();
          if (report.measure) setMeasureReport(report.measure);
          const measured = report.measure ? measureLines(report.measure)[0] : undefined;
          say(measured ? `${syncMessage(report)} · ${measured}` : syncMessage(report));
          // Queued behind this write by the first load's `appWriteChain.idle()`.
          refresh();
          return report;
        }),

      generateReport: (templateId) => {
        let out: ReportPdfVersion | null = null;
        return mutateReport(async () => {
          const created = await api.generateReport(templateId);
          out = created;
          setReportVersions((prev) => [created, ...(prev ?? []).filter((v) => v.id !== created.id)]);
          say(generatedToast(created));
          await reread(async () => setReportVersions(await api.listReportVersions()));
        }).then(() => out);
      },

      setTypeColor: async (id, color) => {
        if (tracksTypeColor(typesRef.current)) {
          const body = await run(async () => {
            const b = await api.patchSurveyType(id, { color });
            setTypes((prev) => replaceType(prev, b));
            return b;
          });
          return body !== null;
        }
        setLocalColors((prev) => {
          const next = setTypeColorIn(prev, id, color);
          writeTypeColors(browserStore(), api.caseId, next);
          return next;
        });
        return true;
      },

      patchProject: (patch) =>
        run(async () => {
          const id = projectRef.current?.id ?? (newProjectId.current ??= newRecordId());
          const body = await api.patchProject(id, patch);
          setProject(() => body);
          refreshShellProject();
          return body;
        }),
    }),
    [
      refresh,
      say,
      run,
      mutateReport,
      reread,
      refreshGate,
      refreshSurvey,
      refreshResources,
      refreshShellProject,
      setTypes,
      setSamples,
      setResources,
      setProject,
      typesRef,
      samplesRef,
      projectRef,
    ],
  );

  // ------------------------------------------------------ survey.changed --

  // Another tab, a colleague or a script wrote to the survey, the samples or
  // the resources (ruxd-6tz; an older server never sends the event). Re-read
  // what the event names, quietly and behind any write of ours on the chain;
  // events landing together (a batch, a merge) fold into one re-read. Our
  // own writes send it too — the re-read then simply confirms them.
  const { surveyChange } = useJobs();
  const pendingChange = useRef<{ survey: boolean; samples: boolean; resources: boolean } | null>(null);
  const lastSeq = useRef<number | null>(null);
  const changeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (changeTimer.current !== null) clearTimeout(changeTimer.current);
    },
    [],
  );
  const handledChange = useRef<typeof surveyChange>(null);
  useEffect(() => {
    if (!surveyChange || !loaded || handledChange.current === surveyChange) return;
    handledChange.current = surveyChange;
    // A gap in `seq` means one was missed: re-read everything.
    const gap = lastSeq.current !== null && surveyChange.seq !== lastSeq.current + 1;
    lastSeq.current = surveyChange.seq;
    const some = (list: readonly unknown[] | undefined) => gap || list === undefined || list.length > 0;
    const want = {
      // Types carry derived figures of their parts and samples: always re-read them.
      survey: true,
      samples: some(surveyChange.sample_ids),
      resources: some(surveyChange.part_codes) || some(surveyChange.type_ids),
    };
    const prev = pendingChange.current;
    if (prev) {
      prev.samples ||= want.samples;
      prev.resources ||= want.resources;
      return;
    }
    pendingChange.current = want;
    changeTimer.current = setTimeout(() => {
      changeTimer.current = null;
      void appWriteChain.idle().then(async () => {
        const w = pendingChange.current;
        pendingChange.current = null;
        if (!w) return;
        await reread(async () => {
          await Promise.all([
            refreshSurvey(),
            w.samples ? api.samples().then((list) => setSamples(() => list)) : null,
            w.resources ? refreshResources() : null,
          ]);
        });
        refreshFigures();
      });
    }, 250);
  }, [surveyChange, loaded, reread, refreshSurvey, refreshResources, refreshFigures, setSamples]);

  const survey = useMemo<Survey | null>(() => (loaded ? { types, counts: countsOf(types) } : null), [loaded, types]);

  const data: CaseData = {
    survey,
    types,
    dismissed,
    dismissSupported,
    typeColorShared,
    samples,
    summary,
    resourceKeys,
    templates,
    resources,
    photos: photosRead.data ?? null,
    project,
    reportVersions,
    fractions,
    buildups: buildupsRead.data ?? null,
    measureReport,
    loading: main.loading,
    loaded,
    error: main.error ?? null,
    busy,
    reportBusy: reportQueue.busy,
    staleNotice,
    canEdit,
  };

  return { data, actions };
}
